import { Check, Inbox, Layers, Loader2 } from "lucide-react";
import { Badge } from "../../../../../components/ui/badge";
import { cn } from "../../../../../lib/utils";
import type { OptroTestCycle } from "../../../../../models/audit/optroSampleImport";

interface OptroTestCycleListProps {
  testCycles: OptroTestCycle[];
  loading: boolean;
  selectedTestCycleId: number | null;
  onSelect: (testCycle: OptroTestCycle) => void;
}

const hasPopulation = (testCycle: OptroTestCycle): boolean =>
  testCycle.sampleCount > 0;

/**
 * Step 1 of the Pull Samples from Optro modal: a single-select list of the
 * linked control's test cycles (E1). Cycles with a sample population are
 * selectable and show a green "N samples" badge; cycles with no population are
 * greyed/non-selectable with a "No population" badge. Mirrors the row/disabled
 * styling of the (removed) file list.
 */
export function OptroTestCycleList({
  testCycles,
  loading,
  selectedTestCycleId,
  onSelect,
}: OptroTestCycleListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-stone-500">
        <Loader2 size={16} className="mr-2 animate-spin" />
        Loading test cycles…
      </div>
    );
  }

  if (testCycles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-stone-500">
        <Inbox size={28} className="mb-3 text-stone-300" />
        <p className="text-sm">No test cycles found for this control</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200">
      {testCycles.map((testCycle) => {
        const selectable = hasPopulation(testCycle);
        const isSelected = selectedTestCycleId === testCycle.id;

        return (
          <li key={testCycle.id}>
            <button
              type="button"
              disabled={!selectable}
              aria-disabled={!selectable}
              aria-pressed={isSelected}
              onClick={() => selectable && onSelect(testCycle)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                selectable
                  ? "cursor-pointer hover:bg-stone-50"
                  : "cursor-not-allowed opacity-60",
                isSelected && "bg-sky-50/50",
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                  isSelected
                    ? "bg-sky-100 text-sky-700"
                    : "bg-stone-100 text-stone-500",
                )}
              >
                <Layers size={15} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium text-stone-900">
                    {testCycle.name}
                  </p>
                  <Badge
                    variant={selectable ? "default" : "secondary"}
                    className={cn(
                      "shrink-0",
                      selectable
                        ? "border-green-200 bg-green-100 text-green-800"
                        : "border-stone-200 bg-stone-100 text-stone-600",
                    )}
                  >
                    {selectable
                      ? `${testCycle.sampleCount} samples`
                      : "No population"}
                  </Badge>
                </div>
              </div>

              {isSelected && (
                <Check size={16} className="shrink-0 text-sky-600" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
